import { KeyValue } from '@angular/common';
import { Component, OnInit, Input } from '@angular/core';
import { BranchChart } from 'src/app/branch-chart';
import ShiftInfo, { getShiftPriorityOrder, TotalShiftPositions } from 'src/app/ShiftInfo';
import { SiteCountry } from 'src/app/site-total.service';

@Component({
  selector: 'app-branch-item',
  templateUrl: './branch-item.component.html',
  styleUrls: ['./branch-item.component.scss']
})

export class BranchItemComponent implements OnInit {

  @Input() branch: BranchChart;
  @Input() branchSites: SiteCountry[] = []; // Consider this as site position assignments
  @Input() positions: Record<number, any[]>; // Consider this as site shift positions
  @Input() futureInspectors: Record<number, number[]>; // key: site ID, value: inspector IDs

  // key: shift name, value: ShiftInfo object type
  shiftsInfo: Record<string, ShiftInfo> = {};

  // Total positions will calculate total of selected fields in each shift
  totalPositions: TotalShiftPositions = {
    unassignedPositions: 0,
    totalAgencies: 0,
    totalInspectors: 0
  };

  constructor() { }

  ngOnInit() {
    // Clear it in case there's a new update
    this.shiftsInfo = {};

    // Goal of this object to count agency assignments based on unique position IDs
    // This will hold shift names as a key and position IDs as a value
    // Do not include it inside branchSites loop because it'll have duplicated shifts that'll affect final count
    // This will hold unique shifts
    const agencyAssignmentIds: Record<string, number[]> = {};

    // A branch can have many sites which can have many shifts. We wanna group those shifts together under this branch
    this.branchSites.forEach(({ sites, siteId }) => {
      // This will get future inspector IDs based on each site ID key
      const shiftFutureInspectors = this.futureInspectors[siteId];

      const sitePositions = this.positions[siteId];

      // key: shift name, value: position IDs
      const shiftPositionIds: Record<string, number[]> = {};

      // A set prevents duplicate entries
      const shiftNames = new Set<string>();

      sitePositions.forEach(position => {
        const shiftName = position.shift;

        // Store all shift names
        shiftNames.add(shiftName);

        // If key exists then push the data
        if (shiftPositionIds[shiftName]) {
          shiftPositionIds[shiftName].push(position.id);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          shiftPositionIds[shiftName] = [position.id];
        }
      });

      if (this.hasNoSites(sites)) {
        sites = [];
      }

      // Goal of this object is to track duplicated inspector IDs for each position
      // This will hold position ID as a key and inspector IDs as a value
      const positionInspectorIds: Record<number, number[]> = {};

      sites.forEach(shiftItem => {
        const shiftName = shiftItem.shift;

        const isInspectorIdDuplicated = this.hasDuplicateInspectorIds(positionInspectorIds, shiftItem);

        // Make sure it doesn't increment values if it holds a duplicate inspector ID case
        if (!isInspectorIdDuplicated) {
          this.shiftsInfo[shiftName] = this.getShiftInfo(shiftName, shiftItem, shiftFutureInspectors);
        }

        // If it's value of that shift key is not an array then make it into an array (in another word, if it doesn't have a value)
        if (!Array.isArray(agencyAssignmentIds[shiftName])) {
          agencyAssignmentIds[shiftName] = [];
        }

        // If shift type is 1 (an agency) and this position ID doesn't exist in the agencyAssignmentIds then add it to that array
        if (shiftItem.type === 1 && !agencyAssignmentIds[shiftName].includes(shiftItem.positionId)) {
          agencyAssignmentIds[shiftName].push(shiftItem.positionId);
        }
      });

      // The same shift name can appear on a different site so make sure you use += instead of = for calculating unassignedPositions
      // In another meaning, since sites are grouped together, you'll have many shifts with the same name
      shiftNames.forEach(shiftName => {
        // If there's no information for the current shift name, then assign empty values to it
        if (!this.shiftsInfo[shiftName]) {
          this.shiftsInfo[shiftName] = this.createEmptyShiftInfo();
        }

        this.shiftsInfo[shiftName].unassignedPositions += this.getUnassignedPositionsCount(shiftPositionIds, sites, shiftName);

        // Not all shifts will have agency assignments
        if (agencyAssignmentIds[shiftName]) {
          // get length of unqiue position IDs and assign them to the totalAgencies
          this.shiftsInfo[shiftName].totalAgencies = agencyAssignmentIds[shiftName].length;
        }
      });

    });

    // At this point, all shifts will be grouped together, that's where we can calculate total positions of those shifts
    this.totalPositions = this.getTotalPositions();
  }

  getShiftInfo(shiftName: string, shift: any, shiftFutureInspectors: number[]): ShiftInfo {
    // If this.shiftsInfo[shiftName] has a value then pick values from there. Otherwise pick the empty values
    const shiftInfo = this.shiftsInfo[shiftName]
      ? this.shiftsInfo[shiftName]
      : this.createEmptyShiftInfo();

    const { unassignedPositions, totalSites, totalInspectors, totalOnSite, totalConfirmed } = shiftInfo;

    // Fill shiftsInfo key with the shiftName and incrementValue based on below conditions
    // shiftFutureInspectors can be undefined if some future inspectors don't exist in the current date
    return {
      unassignedPositions,
      totalSites: shift.type === 0 ? totalSites + 1 : totalSites,
      totalAgencies: 0, // total agencies will not be incremented here, so its value will remain 0
      totalInspectors: shift.type === 0 ? totalInspectors + 1 : totalInspectors,
      totalOnSite: totalOnSite + shift.onSite,
      totalConfirmed: shiftFutureInspectors && shiftFutureInspectors.includes(shift.inspectorId) ? totalConfirmed + 1 : totalConfirmed
    };
  }

  getUnassignedPositionsCount(shiftPositionIds: Record<string, number[]>, sites: any[], shiftName: string): number {
    // We gotta loop through each shift position IDs with their specific shift name in order to check for IDs without assignments
    return shiftPositionIds[shiftName].reduce((previousValue, positionId) => {
      // This will check for each position ID if it has an assignment or not
      const hasPositionAssignment = sites.some(site => {
        // We must loop through each shift and check if shift names match and if their position IDs match
        return site.shift === shiftName && site.positionId === positionId;
      });

      // If that ID doesn't have an assignment then it's an unassigned or empty position
      if (!hasPositionAssignment) {
        return previousValue + 1;
      }

      return previousValue;
    }, 0);
  }

  // Angular will return 2 pairs of type KeyValue which will have the type I specified below
  // First type param of KeyValue will present the key (shift name) and the second one will present the value (ShiftInfo)
  sortShifts(a: KeyValue<string, ShiftInfo>, b: KeyValue<string, ShiftInfo>): number {
    const priorityA = getShiftPriorityOrder(a.key);
    const priorityB = getShiftPriorityOrder(b.key);

    if (priorityA > priorityB) {
      return 1;
    }

    if (priorityA < priorityB) {
      return -1;
    }

    return 0;
  }

  getTotalPositions(): TotalShiftPositions {
    // To avoid confusion, Object.values() transforms an object into array of values
    // reduce() function takes inital values from the second parameter and return it to the first parameter
    // It's mostly used for reduction or incrementing purposes
    // You can search for Javascript Object.values or Javascript reduce function to understand more what's happening here
    return Object.values(this.shiftsInfo).reduce((previousValue, currentValue) => ({
      unassignedPositions: previousValue.unassignedPositions + currentValue.unassignedPositions,
      totalAgencies: previousValue.totalAgencies + currentValue.totalAgencies,
      totalInspectors: previousValue.totalInspectors + currentValue.totalInspectors
    }), this.totalPositions);
    // totalPositions will have 0 value on unassignedPositions, totalAgencies, totalInspectors as an initial value
  }

  hasNoSites(sites: any): boolean {
    // If it's not an array or if it's an array but it's empty
    return !Array.isArray(sites) || (Array.isArray(sites) && !sites.length);
  }

  createEmptyShiftInfo(): ShiftInfo {
    return {
      unassignedPositions: 0,
      totalSites: 0,
      totalAgencies: 0,
      totalInspectors: 0,
      totalOnSite: 0,
      totalConfirmed: 0
    };
  }

  // Returns true if a duplicate inspector ID is found
  hasDuplicateInspectorIds(positionInspectorIds: Record<number, number[]>, shiftAssignment: any): boolean {
    // Check if current assignment doesn't belong to an inspector
    if (shiftAssignment.type !== 0) {
      return false;
    }

    // If positionInspectorIds doesn't hold a key of the position ID then assign the inspector ID to it
    if (!positionInspectorIds[shiftAssignment.positionId]) {
      positionInspectorIds[shiftAssignment.positionId] = [shiftAssignment.inspectorId];
      return false;
    }

    // Check if the position holds the same inspector ID, if so then it's a duplicate ID
    if (positionInspectorIds[shiftAssignment.positionId].includes(shiftAssignment.inspectorId)) {
      return true;
    }

    // otherwise push it to this position ID
    positionInspectorIds[shiftAssignment.positionId].push(shiftAssignment.inspectorId);

    return false;
  }

}
